
import { executeWithTimeout } from '@/lib/supabase';
import { supabase } from '@/lib/supabase/client';
import { DEFAULT_ROLE } from './constants';
import { getRoleFromSessionMetadata, getRoleFromRPC, checkDealerStatus } from './roleUtils';
import { createUserRoleViaRPC, insertUserRoleDirectly } from './roleCreationService';

/**
 * Service for user role-related operations
 */
export const roleService = {
  /**
   * Fetch user role information with improved error handling
   */
  fetchUserRole: async (userId: string) => {
    console.log("[RoleService] Fetching role for user ID:", userId);
    
    if (!userId) {
      console.log("[RoleService] No userId provided, returning default role");
      return DEFAULT_ROLE;
    }
    
    try {
      // Try to get role from JWT token claims
      const sessionRole = await getRoleFromSessionMetadata();
      if (sessionRole) {
        console.log("[RoleService] Using role from session metadata:", sessionRole);
        return sessionRole;
      }
      
      // Use RPC function if available (avoids RLS issues)
      const rpcRole = await getRoleFromRPC(userId);
      if (rpcRole) {
        console.log("[RoleService] Using role from RPC function:", rpcRole);
        return rpcRole;
      }
      
      // Direct query to user_roles table
      try {
        console.log("[RoleService] Attempting direct query to user_roles");
        const { data, error } = await executeWithTimeout(
          async () => await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId)
            .single(),
          5000
        );
        
        if (!error && data && data.role) {
          console.log("[RoleService] Found role via direct query:", data.role);
          return data.role;
        }
      } catch (queryError) {
        console.error("[RoleService] Error with direct user_roles query:", queryError);
      }
      
      // Check if user is a dealer
      const dealerRole = await checkDealerStatus(userId, executeWithTimeout);
      if (dealerRole) {
        console.log("[RoleService] User is a dealer, assigning DEALER role");
        return dealerRole;
      }

      // If all else fails, use default role
      console.log("[RoleService] Falling back to default USER role");
      return DEFAULT_ROLE;
    } catch (error) {
      console.error("[RoleService] Critical error in fetchUserRole:", error);
      return DEFAULT_ROLE;
    }
  },
  
  /**
   * Create default user role
   */
  createUserRole: async (userId: string) => {
    console.log("[RoleService] Creating default role for user:", userId);
    
    if (!userId) {
      console.log("[RoleService] No userId provided, skipping role creation");
      return;
    }
    
    // First check if the user already has a role
    try {
      const { data, error } = await executeWithTimeout(
        async () => await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .single(),
        5000
      );
      
      if (!error && data && data.role) {
        console.log("[RoleService] User already has role:", data.role);
        return;
      }
    } catch (checkError) {
      console.log("[RoleService] Error checking existing role:", checkError);
    }
    
    // User doesn't have a role, create one
    try {
      // Try to use an RPC function if available
      try {
        console.log("[RoleService] Attempting to create role via RPC");
        const roleCreatedViaRPC = await createUserRoleViaRPC(userId);
        if (roleCreatedViaRPC) {
          console.log("[RoleService] Role created via RPC");
          return;
        }
      } catch (rpcError) {
        console.log("[RoleService] Error creating role via RPC:", rpcError);
      }
      
      // Fallback to direct insertion
      console.log("[RoleService] Attempting direct role insertion");
      await insertUserRoleDirectly(userId);
      console.log("[RoleService] Role created via direct insertion");
    } catch (roleError) {
      console.error("[RoleService] Exception during role creation:", roleError);
    }
  }
};
